import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { getBankStatement, getBankStatements } from '@/services/wallet';
import { TStatementBalance } from '@/services/types/WalletData';
import { useStatementCache } from './useStatementCache';
import useAuth from '@/hooks/useAuth';

export type TPdfError = {
  message: string;
  status: number;
  isRetryable: boolean;
};

export type TMonthlyStatementRow = TStatementBalance & {
  pdfUrl?: string;
  isPdfLoading: boolean;
  pdfError?: TPdfError;
};

export const useMonthlyStatements = (walletId: string) => {
  const { accountId } = useAuth()!;
  const { getCache, setBalances, setPdfUrl } = useStatementCache(accountId, walletId);

  const [isLoading, setIsLoading] = useState(true);
  const [isYearChanging, setIsYearChanging] = useState(false);
  const [firstDate, setFirstDate] = useState<Date | null>(null);
  const [lastDate, setLastDate] = useState<Date | null>(null);
  const [defaultBalances, setDefaultBalances] = useState<TStatementBalance[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [pdfUrls, setPdfUrls] = useState<Record<string, string>>({});
  const [pdfLoadingKeys, setPdfLoadingKeys] = useState<Set<string>>(new Set());
  const [pdfErrors, setPdfErrors] = useState<Record<string, TPdfError>>({});
  const [allBalances, setAllBalances] = useState<TStatementBalance[] | null>(null);

  const isFetchingAll = useRef(false);
  // Keyed by "year-month". Lets a concurrent caller (a batch download reaching a row that's
  // already downloading singly, or a double-clicked "Download selected") await the same
  // in-flight request instead of bailing out early and being wrongly counted as a failure. A Map
  // (rather than a plain Record) so `.get()` is correctly typed as possibly undefined.
  const inFlightRequests = useRef<Map<string, Promise<{ url?: string; error?: TPdfError }>>>(
    new Map()
  );

  useEffect(() => {
    if (!walletId) return;

    setIsLoading(true);
    setAllBalances(null);
    isFetchingAll.current = false;
    setSelectedYear(null);

    const cached = getCache();

    if (cached) {
      setAllBalances(cached.balances);
      setPdfUrls(cached.pdfUrls);

      if (cached.firstDate) setFirstDate(new Date(cached.firstDate));
      if (cached.lastDate) setLastDate(new Date(cached.lastDate));

      const cutoff = moment().subtract(11, 'months').startOf('month');
      const last12 = cached.balances.filter((b) =>
        moment(`${b.year}-${b.month}`, 'YYYY-M').isSameOrAfter(cutoff, 'month')
      );
      setDefaultBalances(last12);
      setIsLoading(false);
      return;
    }

    getBankStatements(walletId, 12)
      .then((res) => {
        if (!res?.data) return;
        setFirstDate(new Date(res.data.firstDate));
        setLastDate(new Date(res.data.lastDate));
        const balances = res.data.statementBalances ?? [];
        setDefaultBalances(balances);
        setBalances(balances, res.data.firstDate, res.data.lastDate);
      })
      .finally(() => setIsLoading(false));
  }, [walletId]);

  const handleYearChange = useCallback(
    async (year: number | null) => {
      setSelectedYear(year);

      if (year !== null && !allBalances && !isFetchingAll.current) {
        isFetchingAll.current = true;
        setIsYearChanging(true);
        try {
          const res = await getBankStatements(walletId, -1);
          if (res?.data) {
            const balances = res.data.statementBalances ?? [];
            setAllBalances(balances);
            setBalances(balances, res.data.firstDate, res.data.lastDate);

            if (!firstDate) setFirstDate(new Date(res.data.firstDate));
            if (!lastDate) setLastDate(new Date(res.data.lastDate));
          }
        } finally {
          isFetchingAll.current = false;
          setIsYearChanging(false);
        }
      }
    },
    [walletId, allBalances, firstDate, lastDate, setBalances]
  );

  // Returns the outcome directly (rather than leaving the caller to re-read it back off
  // displayedBalances afterwards) because a batch download awaits these sequentially - by the
  // time one call resolves, the caller's own displayedBalances closure is stale (captured before
  // any of this batch's state updates landed), so re-deriving success/error from it would silently
  // misreport every failure as neither retryable nor permanent.
  const fetchPdfUrl = useCallback(
    (year: number, month: number): Promise<{ url?: string; error?: TPdfError }> => {
      const key = `${year}-${month}`;
      if (pdfUrls[key]) return Promise.resolve({ url: pdfUrls[key] });
      const existingRequest = inFlightRequests.current.get(key);
      if (existingRequest) return existingRequest;

      const request = (async (): Promise<{ url?: string; error?: TPdfError }> => {
        setPdfLoadingKeys((prev) => new Set(prev).add(key));
        setPdfErrors((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });

        try {
          const res = await getBankStatement(walletId, year, month);
          const url = res?.data?.bankStatement;

          if (url) {
            setPdfUrls((prev) => ({ ...prev, [key]: url }));
            setPdfUrl(year, month, url);
            return { url };
          } else {
            const errorInfo: TPdfError = {
              message: 'Failed to generate PDF',
              status: 500,
              isRetryable: true
            };
            setPdfErrors((prev) => ({ ...prev, [key]: errorInfo }));
            return { error: errorInfo };
          }
        } catch (error: unknown) {
          let errorInfo: TPdfError;

          if (error && typeof error === 'object' && 'httpStatus' in error) {
            const errObj = error as Record<string, unknown>;
            const status = Number(errObj.httpStatus);
            const message = String(errObj.message || 'Failed to download statement');
            errorInfo = {
              message,
              status,
              isRetryable: status >= 500
            };
          } else if (
            error &&
            typeof error === 'object' &&
            typeof (error as Response).status === 'number'
          ) {
            // BaseApi.parseResponse throws the raw Response (instead of a parsed body with
            // httpStatus) when the error response isn't valid JSON - e.g. a gateway 502/503 with
            // an HTML or empty body. Its own numeric `.status` is the real HTTP status; this is
            // distinguishable from a parsed ErrorResponseBody, whose `status` is a string enum
            // name.
            const status = (error as Response).status;
            errorInfo = {
              message: 'Failed to download statement',
              status,
              isRetryable: status >= 500
            };
          } else {
            errorInfo = {
              message: error instanceof Error ? error.message : 'Failed to download statement',
              status: 0,
              isRetryable: false
            };
          }

          setPdfErrors((prev) => ({ ...prev, [key]: errorInfo }));
          return { error: errorInfo };
        } finally {
          setPdfLoadingKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
          inFlightRequests.current.delete(key);
        }
      })();

      inFlightRequests.current.set(key, request);
      return request;
    },
    [walletId, pdfUrls, setPdfUrl]
  );

  const source = selectedYear !== null ? allBalances ?? defaultBalances : defaultBalances;

  // Ordering is owned by the DataTable (client-side sort on the Statements column).
  const displayedBalances: TMonthlyStatementRow[] = (
    selectedYear !== null ? source.filter((b) => b.year === selectedYear) : source
  ).map((b) => {
    const key = `${b.year}-${b.month}`;
    return {
      ...b,
      pdfUrl: pdfUrls[key],
      isPdfLoading: pdfLoadingKeys.has(key),
      pdfError: pdfErrors[key]
    };
  });

  const clearPdfError = useCallback((year: number, month: number) => {
    const key = `${year}-${month}`;
    setPdfErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  return {
    isLoading,
    isYearChanging,
    firstDate,
    lastDate,
    displayedBalances,
    selectedYear,
    handleYearChange,
    fetchPdfUrl,
    clearPdfError
  };
};
